import { FormGroup } from '@angular/forms';
import convert from 'heic-convert/browser';

import { Block } from '../../planogram/models/block';
import { Element } from '../../equipment/models/elements/element';
import { Equipment } from '../../equipment/models/equipment';
import { ProductGroup } from '../../planogram/models/product-group';
import { ProductGroupRotation } from '../../planogram/models/product-group-rotation';
import { Trapeze } from '../../equipment/models/figures/trapeze';
import { Point } from '../models/point';
import { Product } from '../../planogram/models/product';
import { IPlanogramBlockData } from '../../planogram/models/planogramData';
import { IColumnType } from '../models/table';
import { EquipmentElements } from '../../equipment/models/elements/equipmentElements';
import { IPlanogram } from '../../planogram/models/planogram';
import { IPlanogramBoundingWithBlocks } from '../models/planogramBoundingWithBlocks';
import { SearchAlgorithmType } from '../models/personalSettings';
import { GlobalSettings } from '../models/globalSettings';
import { IProperty, ITreeProperty } from '../../planogram/models/product-base';
import { IPlanogramBounds } from '../../planogram/planogram-edit/models/planogramBounds';
import { EquipmentBase } from '../../planogram/models/equipment-base';
import { POSImageMaterial } from '../../planogram/models/POSMaterials/POSImageMaterial';
import { POSMaterial } from '../../planogram/models/POSMaterials/POSMaterial';

const DEFAULT_DATE_FORMAT: string = 'DD-MM-YYYY',
	EPS: number = 0.02;

interface ISortParameters {
	firstEl: any;
	secondEl: any;
	order: string | boolean; // shit
	displayValue?: string;
	dateFormat?: string;
	disabledFirstEl?: boolean;
	disabledSecondEl?: boolean;
}

interface IChildren<T> {
	id?: number | string;
	children?: Array<T>;
}

interface IParent<T> extends IChildren<T> {
	parent?: T;
}

interface IChecked<T> extends IParent<T> {
	checked: boolean;
	id?: number;
	name?: string;
	checkedShadow?: boolean;
}

interface IOpened<T> extends IChildren<T> {
	opened: boolean;
}

export class Utils {

	static aggregate(list: Array<any>, byFields: Array<string>, aggFields: Array<string>, aggFunction: Function): Array<any> {
		const by: string = byFields[0];

		let tempList: Array<any> = [],
			resultList: Array<any> = [];

		list.sort((a: any, b: any) => a[by] - b[by]);

		for (let i = 0; i < list.length; i++) {
			tempList.push(list[i]);
			if (i != list.length - 1 && list[i][by] == list[i + 1][by]) continue;
			let resultObj = {};
			byFields.forEach(field => resultObj[field] = list[i][field]);
			aggFields.forEach(field => resultObj[field] = aggFunction(tempList.map(item => item[field])));
			resultList.push(resultObj);
			tempList = [];
		}

		return resultList;
	}

	static arrayDiff<T>(arr1: Array<T>, arr2: Array<T>): Array<T> {
		if (!arr1.length) return arr2;
		if (!arr2.length) return arr1;

		return arr1.filter((item: T) => !arr2.includes(item));
	}

	static arrayDivision(
		array: Array<any>,
		order: any,
		index: any,
		isPlanPanels: boolean = false,
		dateFormat?: string
	): Array<any> {
		if (order == true) order = 'asc';
		if (order == false) order = 'desc';

		const arrStrings = [],
			arrNumbers = [],
			arrSymbols = [],
			regExpForSymbol = /^[-—!$%^&*()_+|~=`{}\[\]:";'<>?,.\/]/,
			firstZeroRegExp = /^0/;
		let isPanelsSortingInPlanEdit: boolean = false;

		array.map((el) => {
			isPanelsSortingInPlanEdit = !!el[index] || el[index] == '' || isPlanPanels;
			const element = el.data ? el.data[index].sortingValue : el[index];
			if (regExpForSymbol.test(element)) return arrSymbols.push(el);
			if (
				((!isNaN(Number(element)) && !firstZeroRegExp.test(element))) ||
				element == '' ||
				element == null ||
				element == 'GENERAL' ||
				(element.length == 1 && firstZeroRegExp.test(element))
			) {
				return arrNumbers.push(el);
			} else {
				return arrStrings.push(el);
			}
		});
		arrStrings.sort((a, b) =>
			Utils.generalSorting({
				firstEl: isPanelsSortingInPlanEdit
					? String(a[index])
					: String(a.data[index].sortingValue),
				secondEl: isPanelsSortingInPlanEdit
					? String(b[index])
					: String(b.data[index].sortingValue),
				order: order == 'asc' ? 'desc' : 'asc',
				dateFormat
			})
		);
		arrNumbers.sort((a, b) =>
			Utils.sortingNumbers({
				firstEl: isPanelsSortingInPlanEdit ? a[index] : a.data[index].sortingValue,
				secondEl: isPanelsSortingInPlanEdit ? b[index] : b.data[index].sortingValue,
				order: order == 'asc' ? 'desc' : 'asc'
			})
		);
		arrSymbols.sort((a, b) =>
			Utils.generalSorting({
				firstEl: isPanelsSortingInPlanEdit ? a[index] : a.data[index].sortingValue,
				secondEl: isPanelsSortingInPlanEdit ? b[index] : b.data[index].sortingValue,
				order: order == 'asc' ? 'desc' : 'asc',
				dateFormat
			})
		);
		array =
			order == 'asc'
				? arrSymbols.concat(arrNumbers, arrStrings)
				: arrStrings.concat(arrNumbers, arrSymbols);

		return array;
	}

	static assignObject(object1: any, object2: any): void {
		for (const prop in object2) {
			if (object1.hasOwnProperty(prop)) {
				if (object2[prop] instanceof Object && !Array.isArray(object2[prop]) && object2[prop] !== null) {
					if (!(object1[prop] instanceof Object)) object1[prop] = {};
					this.assignObject(object1[prop], object2[prop]);
				} else if (Array.isArray(object2[prop]))
					object1[prop] = [...object2[prop]];
				else object1[prop] = object2[prop];
			} else {
				if (object2[prop] instanceof Object && !Array.isArray(object2[prop]) && object2[prop] !== null) {
					object1[prop] = { ...object2[prop] };
				} else if (Array.isArray(object2[prop]))
					object1[prop] = [...object2[prop]];
				else object1[prop] = object2[prop];
			}
		}
	}

	static avgProductPrice(price: string): number {
		const prices: Array<number> = price?.split('-').map((value: string) => +value);
		if (!prices?.length) return 0;
		return Utils.sum(prices) / prices.length;
	}

	static bruteCheckFileExtension(filename) {
		return ['png', 'jpg', 'jpeg'].includes(filename.split('.').slice(-1)[0].toLowerCase());
	}

	static calculateColumnsSize(columns: Array<IColumnType>, visibleColumnsCount: number): void {
		const invisibleColumns: Array<number> = columns
			.filter((column: IColumnType) => !column.visible)
			.map((column: IColumnType) => column.size ?? (100 / columns.length));

		const visibleColumnsSizeSum: number = Utils.sum(columns
			.map((column: IColumnType) => column.size ?? (100 / columns.length)));

		columns.forEach((column: IColumnType) => {
			if (column.button) return;
			column.size += (100 - visibleColumnsSizeSum) / visibleColumnsCount;
			if (invisibleColumns.length) {
				column.displaySize = column.size + (Utils.sum(invisibleColumns) / visibleColumnsCount);
			} else {
				column.displaySize = column.size;
			}
		});
	}

	static calculatePlanogramBounds(planogram: EquipmentBase): IPlanogramBounds {
		let minX: number = 0;
		let minY: number = 0;
		let maxX: number = planogram.Width;
		let maxY: number = planogram.Height;

		// Include Products
		if (planogram.products?.length) {
			planogram.products.forEach((product: ProductGroup): void => {
				const boundingX = product.getBoundingX();
				const boundingY = product.getBoundingY();

				minX = Math.min(minX, boundingX.min);
				minY = Math.min(minY, boundingY.min);
				maxX = Math.max(maxX, boundingX.max);
				maxY = Math.max(maxY, boundingY.max);
			});
		}

		// Include Blocks
		if (planogram.blocks?.length) {
			planogram.blocks.forEach((block: Block): void => {
				const blockMinX: number = block.begin[0].x;
				const blockMinY: number = planogram.Height - block.begin[0].y - block.height;
				const blockMaxX: number = block.begin[0].x + block.width;
				const blockMaxY: number = -block.begin[0].y + planogram.Height;

				minX = Math.min(minX, blockMinX);
				minY = Math.min(minY, blockMinY);
				maxX = Math.max(maxX, blockMaxX);
				maxY = Math.max(maxY, blockMaxY);
			});
		}

		// Include POS Materials
		if (planogram.POSMaterials?.length) {
			planogram.POSMaterials.forEach((material: POSMaterial): void => {
				const materialHeight: number = material.height || 0.04;
				const origin: [number, number] = [material.x, material.y];
				const corners: Array<[number, number]> = [
					[material.x, material.y],
					[material.x + material.width, material.y],
					[material.x + material.width, material.y + materialHeight],
					[material.x, material.y + materialHeight]
				].map((corner: [number, number]) => Utils.rotatePoint(origin, corner, material.angle));

				const xs: Array<number> = corners.map((corner: [number, number]) => corner[0]);
				const ys: Array<number> = corners.map((corner: [number, number]) => corner[1]);
				const materialMinX: number = Math.min(...xs);
				const materialMaxX: number = Math.max(...xs);
				const materialMinY: number = planogram.Height - Math.max(...ys);
				const materialMaxY: number = planogram.Height - Math.min(...ys);

				minX = Math.min(minX, materialMinX);
				minY = Math.min(minY, materialMinY);
				maxX = Math.max(maxX, materialMaxX);
				maxY = Math.max(maxY, materialMaxY);
			});
		}

		return {
			width: maxX - minX,
			height: maxY - minY,
			minX,
			minY,
			maxX,
			maxY
		};
	}

	static center(x1: number, y1: number, x2: number, y2: number): Point {
		return {
			x: (x1 + x2) / 2,
			y: (y1 + y2) / 2
		};
	}

	static characterSorting(sortParam: ISortParameters): number {
		const ukrRegExp = /[А-ЯҐЄІЇ]/i;

		if (
			sortParam.firstEl !== true && sortParam.firstEl !== false &&
			sortParam.secondEl !== true && sortParam.secondEl !== false &&
			typeof sortParam.firstEl !== 'object' &&
			typeof sortParam.secondEl !== 'object'
		) {
			if (ukrRegExp.test(sortParam.firstEl) || ukrRegExp.test(sortParam.secondEl)) {
				const aLowerCaseValue = String(sortParam.firstEl).toLowerCase(),
					bLowerCaseValue = String(sortParam.secondEl).toLowerCase();
				return sortParam.order == 'desc' ?
					aLowerCaseValue.localeCompare(bLowerCaseValue, 'ua') :
					bLowerCaseValue.localeCompare(aLowerCaseValue, 'ua');
			} else {
				const aLowerCaseValue = String(sortParam.firstEl).toLowerCase(),
					bLowerCaseValue = String(sortParam.secondEl).toLowerCase();
				return sortParam.order == 'desc' ?
					aLowerCaseValue.localeCompare(bLowerCaseValue) :
					bLowerCaseValue.localeCompare(aLowerCaseValue);
			}
		}
	}

	static checkItems<T extends IChecked<T> = any>(elements: Array<T>, ids: Array<number>): void {
		this.setParent(elements);
		this.rec(elements, (element: T) => {
			element.checked = ids.includes(element.id) || element.checked;
			if (element.checked) {
				this.childrenCheck(element);
				this.parentCheck(element);
			}
		});
	}

	static childrenCheck<T extends IChecked<T> = any>(initElement: T): void {
		this.rec<T>([initElement], (element: T) => {
			if (element.children && element.children.length) {
				element.children.forEach((child: T) => child.checked = element.checked);
			}
			if (element.children) {
				element.checkedShadow = element.children.filter((child: T) => child.checked || child.checkedShadow).length > 0;
			}
		});
	}

	static common<T = any>(list: Array<T>): T {
		let count: number = 1,
			item: T = list[0];

		list.forEach((element: T) => {
			const cnt: number = list.filter((el: T) => el == element).length;

			if (cnt > count) {
				count = cnt;
				item = element;
			}
		});

		return item;
	}

	static convertPercentToRadius(percent: number, element: Element): number {
		return Utils.round(Math.min(element.Width, element.Depth) * percent / 100);
	}

	static dataURLtoFile(dataURL: string, filename: string): File {
		if (!dataURL || !dataURL.includes(',')) return new File([], filename, { type: 'image/png' });

		let arr = dataURL.split(','),
			mime = arr[0].match(/:(.*?);/)[1],
			bstr = atob(arr[1]),
			n = bstr.length,
			u8arr = new Uint8Array(n);

		while (n--) {
			u8arr[n] = bstr.charCodeAt(n);
		}

		return new File([u8arr], filename, { type: mime });
	}

	static dateDayComparison(dateA: Date, dateB: Date): boolean {
		const yearA = dateA.getFullYear(),
			monthA = dateA.getMonth(),
			dayA = dateA.getDate();

		const yearB = dateB.getFullYear(),
			monthB = dateB.getMonth(),
			dayB = dateB.getDate();

		return new Date(yearA, monthA, dayA) > new Date(yearB, monthB, dayB);
	}

	static deepCopy(obj: any): any {
		if (!Array.isArray(obj)) {
			let responseObj = {};
			for (let key in obj) {
				if (typeof obj[key] == 'object' && obj[key] != null) {
					responseObj[key] = Utils.deepCopy(obj[key]);
					continue;
				}
				responseObj[key] = obj[key];
			}
			return responseObj;
		} else {
			let responseObj = [];
			obj.forEach(element => {
				if (typeof element == 'object') {
					responseObj.push(Utils.deepCopy(element));
					return;
				}
				responseObj.push(element);
			});
			return responseObj;
		}
	}

	static distance(x1: number, y1: number, x2: number, y2: number, positions: number = GlobalSettings.ACCURACY): number {
		return Utils.round(Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2)), positions);
	}

	static distancePointToSegment(point: Point, segment: [Point, Point], accuracy: number = GlobalSettings.ACCURACY): number {
		const [segmentStart, segmentEnd] = segment;
		const lineLengthSquared: number = Math.pow(segmentEnd.x - segmentStart.x, 2) + Math.pow(segmentEnd.y - segmentStart.y, 2);

		if (lineLengthSquared === 0) {
			return Utils.distance(segmentStart.x, segmentStart.y, point.x, point.y, accuracy);
		}

		let projectionFactor: number = ((point.x - segmentStart.x) * (segmentEnd.x - segmentStart.x) +
			(point.y - segmentStart.y) * (segmentEnd.y - segmentStart.y)) / lineLengthSquared;

		projectionFactor = Math.max(0, Math.min(1, projectionFactor));

		const projection: Point = {
			x: segmentStart.x + projectionFactor * (segmentEnd.x - segmentStart.x),
			y: segmentStart.y + projectionFactor * (segmentEnd.y - segmentStart.y)
		};

		return Utils.distance(projection.x, projection.y, point.x, point.y, accuracy);
	}

	static equalLists(list1: Array<any>, list2: Array<any>): boolean {
		if (list1.length != list2.length) return false;

		list1.sort((a: any, b: any) => a - b);
		list2.sort((a: any, b: any) => a - b);

		for (let i = 0; i < list1.length; i++) {
			if (list1[i] != list2[i]) return false;
		}

		return true;
	}

	static equalObjects(obj1: any, obj2: any, calculateDiff?: boolean): boolean {
		if (Object.keys(obj1).length != Object.keys(obj2).length) return false;
		for (let key in obj1) {
			if (calculateDiff) {
				if (obj1[key] !== obj2[key] && Math.abs(obj1[key] - obj2[key]) > EPS) {
					return false;
				}
				continue;
			}

			if (key == 'childrens') {
				return Utils.equalObjectLists(obj1[key], obj2[key]);
			}

			if (Array.isArray(obj1[key]) && Array.isArray(obj2[key])) {
				if (!Utils.equalLists(obj1[key], obj2[key])) return false;
			} else if (obj1[key] !== obj2[key]) {
				return false;
			}
		}
		return true;
	}

	static equalObjectLists(list1: Array<any>, list2: Array<any>): boolean {
		if (list1.length != list2.length) return false;

		for (let i = 0; i < list1.length; i++) {
			if (!Utils.equalObjects(list1[i], list2[i])) return false;
		}

		return true;
	}

	static findDependProducts(product: ProductGroup, products: Array<ProductGroup>): Array<ProductGroup> {
		return products.filter((pg: ProductGroup) =>
			product.begin.x == pg.begin.x &&
			product.begin.z == pg.begin.z &&
			product.getBoundingY().min < pg.getBoundingY().min &&
			product.element == pg.element &&
			product !== pg
		);
	}

	static findDuplicates(data: Array<string>): Array<any> {
		let result: Array<any> = [];

		data.forEach((element: string, index: number) => {
			if (data.indexOf(element, index + 1) > -1) {
				if (result.indexOf(element) === -1) {
					result.push(element.split(','));
				}
			}
		});

		return result;
	}

	static findEventParent(target: any, className: string): any {
		if (target.className == className || (target.classList && target.classList.contains(className))) return target;

		if (!target.parentNode) return null;

		return Utils.findEventParent(target.parentNode, className);
	}

	static findTreeElementById<T extends IChildren<T>>(elements: Array<T>, targetId: number): T | null {
		if (!elements || !elements.length) return null;

		const findElement: T | undefined = elements.find((element: T) => element.id === targetId);
		if (findElement) return findElement;

		for (const element of elements) {
			const findElement: T | null = this.findTreeElementById(element.children, targetId);
			if (findElement) return findElement;
		}

		return null;
	}

	static findTreeElementChildrenIds<T extends IChildren<T>>(element: T): Array<number> {
		if (!element) return [];

		const result: Array<number> = [];

		for (const child of element.children) {
			result.push(child.id as number);
			result.push(...this.findTreeElementChildrenIds(child));
		}

		return result;
	}

	static formatNumber(value: number | string, position: number = GlobalSettings.ACCURACY): string {
		if (typeof value != 'number') return value;

		value = String(parseFloat(value.toFixed(position)));
		let number: string = value.split('.')[0].split('').reverse().join(''),
			decimal: string = value.split('.')[1],
			temp: string = '';

		for (let index = 0; index < number.length; index++) {
			temp += number[index];
			if (((index + 1) % 3 == 0) && (index != number.length - 1) && (number[index + 1] != '-'))
				temp += ' ';
		}
		temp = temp.split('').reverse().join('');
		if (decimal) temp += '.' + decimal;
		return temp;
	}

	static generalSorting(sortParam: ISortParameters): number {

		const firstZeroRegExp = /^0/;

		if (sortParam.order == true) {
			sortParam.order = 'asc';
		}

		if (sortParam.order == false) {
			sortParam.order = 'desc';
		}

		if (['—', '-', '', null, 'GENERAL'].includes(sortParam.firstEl)) {
			sortParam.firstEl = '-100000000000';
		}

		if (['—', '-', '', null, 'GENERAL'].includes(sortParam.secondEl)) {
			sortParam.secondEl = '-100000000000';
		}

		if (sortParam.displayValue == 'barcode') {
			return this.sortingBarcode({
				firstEl: sortParam.firstEl,
				secondEl: sortParam.secondEl,
				order: sortParam.order
			});
		}

		if (this.parseSortableDate(sortParam.firstEl, sortParam.dateFormat) &&
			this.parseSortableDate(sortParam.secondEl, sortParam.dateFormat)) {
			return this.sortingDates({
				firstEl: sortParam.firstEl,
				secondEl: sortParam.secondEl,
				order: sortParam.order,
				dateFormat: sortParam.dateFormat || DEFAULT_DATE_FORMAT
			});
		}

		if (
			(!isNaN(Number(sortParam.firstEl)) && !firstZeroRegExp.test(sortParam.firstEl) &&
				!isNaN(Number(sortParam.secondEl)) && !firstZeroRegExp.test(sortParam.secondEl)) ||
			((sortParam.firstEl && sortParam.firstEl.length == 1 && firstZeroRegExp.test(sortParam.firstEl)) &&
				(sortParam.secondEl && sortParam.secondEl.length == 1 && firstZeroRegExp.test(sortParam.secondEl)))
		) {
			return this.sortingNumbers({
				firstEl: sortParam.firstEl,
				secondEl: sortParam.secondEl,
				order: sortParam.order
			});
		}

		return this.characterSorting({
			firstEl: sortParam.firstEl,
			secondEl: sortParam.secondEl,
			order: sortParam.order
		});
	}

	static getDependProduct(product: any, standProducts: Array<ProductGroup>, element: Element): ProductGroup | undefined {
		return standProducts.find((pg: ProductGroup) =>
			element == pg.element &&
			pg.product.id === product.depend_product &&
			pg.begin.x === product.position_x &&
			pg.begin.z === product.position_z
		);
	}

	static getDiffBetweenTopElements(stand: any): number {
		const sortedStandElements: Array<Element> = Utils.getSortedElements([...stand.orderedElements]);
		let topPoint: number = sortedStandElements[0].getBoundingY().max;

		if (stand.products.length) {
			const sortedProducts: Array<ProductGroup> = Utils.getSortedProducts(stand.products.filter(pg => !pg.isLooseForm));
			if (sortedProducts.length)
				topPoint = Utils.max([sortedProducts[0].getBoundingY().max, topPoint]);
		}

		if (stand.blocks.length) {
			stand.blocks.forEach((block: Block): void => {
				topPoint = Utils.max([stand.Height - block.begin[0].y, topPoint]);
			});
		}

		if (stand.POSMaterials?.length) {
			stand.POSMaterials.forEach((material: POSImageMaterial): void => {
				topPoint = Utils.max([stand.Height - material.y, topPoint]);
			});
		}

		return Utils.round(topPoint - sortedStandElements[0].getBoundingY().max);
	}

	static getDirtyFormValues(form: FormGroup): any {
		let dirtyValues = {};

		Object.keys(form.controls)
			.forEach(key => {
				const currentControl = form.controls[key];

				if (currentControl.dirty || key === 'id') {
					dirtyValues[key] = currentControl['controls'] ?
						currentControl['controls']
							.map(control => Utils.getDirtyFormValues(control))
							.filter(control => Object.keys(control).length > 1) :
						currentControl.value;
				}
			});

		return dirtyValues;
	}

	// функція повертає номер елементу на обладнанні
	static getElementIndex(element: Element, instrument: Equipment): number {
		type CommonType = { common: Array<string>, sort: (a: Element, b: Element) => number };

		const commonTypes: { [key: string]: CommonType } = {
			[EquipmentElements.Shelf]: {
				common: [EquipmentElements.AngleShelf, EquipmentElements.Shelf, EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze],
				sort: (a: Element, b: Element) => {
					return Utils.round(a.shapes[0].begin.y) - Utils.round(b.shapes[0].begin.y);
				}
			},
			[EquipmentElements.AngleShelf]: {
				common: [EquipmentElements.AngleShelf, EquipmentElements.Shelf, EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze],
				sort: (a: Element, b: Element) => {
					return Utils.round(a.shapes[0].begin.y) - Utils.round(b.shapes[0].begin.y);
				}
			},
			[EquipmentElements.HangBar]: {
				common: [EquipmentElements.HangBar],
				sort: (a: Element, b: Element) => {
					return Utils.round(a.shapes[0].begin.y) - Utils.round(b.shapes[0].begin.y);
				}
			},
			[EquipmentElements.Hook]: {
				common: [EquipmentElements.Hook],
				sort: (a: Element, b: Element) => {
					return Utils.round(b.shapes[0].begin.y) - Utils.round(a.shapes[0].begin.y);
				}
			},
			[EquipmentElements.HookPanel]: {
				common: [EquipmentElements.HookPanel],
				sort: (a: Element, b: Element) => {
					return Utils.round(b.shapes[0].begin.y) - Utils.round(a.shapes[0].begin.y);
				}
			},
			[EquipmentElements.Perforation]: {
				common: [EquipmentElements.Perforation],
				sort: (a: Element, b: Element) => {
					return Utils.round(b.shapes[0].begin.y) - Utils.round(a.shapes[0].begin.y);
				}
			},
			[EquipmentElements.Box]: {
				common: [EquipmentElements.Box, EquipmentElements.SlicedBox],
				sort: (a: Element, b: Element) => {
					return Utils.round(a.shapes[0].begin.z) - Utils.round(b.shapes[0].begin.z);
				}
			},
			[EquipmentElements.SlicedBox]: {
				common: [EquipmentElements.Box, EquipmentElements.SlicedBox],
				sort: (a: Element, b: Element) => {
					return Utils.round(a.shapes[0].begin.z) - Utils.round(b.shapes[0].begin.z);
				}
			}
		};

		if (!Object.keys(commonTypes).includes(element.parent.type)) return 1;

		const commonType: CommonType = commonTypes[element.parent.type];

		const availableElements: Array<Element> =  instrument.availableElements
			.filter((el: Element) => commonType.common.includes(el.parent.type) && (el.depend == element.depend || element.getDepends().includes(el)));
		const dependElement: Element = element.depend;
		const baseWithCentralPillar: boolean = instrument.allElements
			.filter((el: Element) => el.getDepends().includes(dependElement))
			.some((el: Element) => el.parent.is(EquipmentElements.CentralPillar));
		const twoSided: boolean = dependElement?.depend?.parent.is(EquipmentElements.CentralPillar) || baseWithCentralPillar;

		if ([EquipmentElements.Box, EquipmentElements.SlicedBox].includes(element.parent.type) && twoSided) {
			const centerZ: number = dependElement.center.z;
			const frontBoxes: Array<Element> = availableElements
				.filter((el: Element) => (el.shapes[0].begin.z + el.shapes[0].depth) >= centerZ);
			const backBoxes: Array<Element> = availableElements
				.filter((el: Element) => (el.shapes[0].begin.z + el.shapes[0].depth) < centerZ);

			return [
				...frontBoxes.sort((a: Element, b: Element) =>
					commonType.sort(a, b) ||
					Utils.round(a.shapes[0].begin.x) - Utils.round(b.shapes[0].begin.x)
				),
				...backBoxes.sort((a: Element, b: Element) =>
					commonType.sort(b, a) ||
					Utils.round(b.shapes[0].begin.x) - Utils.round(a.shapes[0].begin.x)
				)
			].indexOf(element) + 1;
		}

		return availableElements
			.sort((a: Element, b: Element) =>
				commonType.sort(a, b) ||
				Utils.round(a.shapes[0].begin.x) - Utils.round(b.shapes[0].begin.x)
			)
			.indexOf(element) + 1;
	}

	static getItemSides(corners: Array<Point>): Array<[Point, Point]> {
		const sides: Array<[Point, Point]> = [];
		for (let i = 0; i < corners.length; i++) {
			const nextIndex = (i + 1) % corners.length;
			sides.push([corners[i], corners[nextIndex]]);
		}
		return sides;
	}

	static getNearestPoint(anchorPoints: Array<number>, point: number): number {
		const deltas: Array<number> = anchorPoints.map((anchorPoint: number) => {
				return Math.abs(Utils.round(anchorPoint - point));
			}),
			minDelta: number = Math.min(...deltas);

		return anchorPoints[deltas.findIndex((delta: number) => delta == minDelta)] || point;
	}

	static getNewName(nameList: Array<string>, name: string, copyText: string, maxLength?: number): string {
		const numberInBracketsRegExp = /(\(\d+\))/igm,
			numberRegExp = /\d+/,
			nameReplace = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
			nameNumberRegExp = new RegExp(nameReplace + ' ' + copyText + ' ' + '\\(' + '\\d+' + '\\)');
		let newName = name;

		if (nameList.length) {
			const namesWithNums = nameList
				.filter(n => n == `${name} ${copyText}`)
				.concat(Utils.to1DArray(nameList
					.sort()
					.map(n => {
						return n.match(nameNumberRegExp);
					})
					.filter((n, i) => !!n && nameList.indexOf(n[0]) === i)));
			if (!namesWithNums.length || namesWithNums[0] != `${name} ${copyText}`) {
				newName = `${name} ${copyText}`;
			} else {
				const foundNums = namesWithNums
						.map(n => {
							if (!!n.match(nameNumberRegExp))
								return n.match(numberInBracketsRegExp).slice(-1);
						})
						.filter(n => !!n),
					numbers = Utils.to1DArray(foundNums)
						.map(n => +n.match(numberRegExp))
						.sort((a, b) => a - b);
				if (!numbers.length || numbers[0] != 1) {
					newName = `${name} ${copyText} (1)`;
				} else {
					const lastNum = numbers.find((n, i) => (numbers[i + 1] != n + 1));
					newName = `${name} ${copyText} (${lastNum + 1})`;
				}
			}
		}

		if (maxLength && newName.length > maxLength) newName = newName.slice(0, maxLength);
		return newName;
	}

	// метод, який повертає координати канви для планограми, включаючи блоки
	static getPlanogramBoundingWithBlocks(
		planogram: IPlanogram,
		canvasOffset: number
	): IPlanogramBoundingWithBlocks {
		if (planogram.detalisation) return Utils.getPlanogramDetailedBoundingWithBlocks(planogram, canvasOffset);

		const scale: number = planogram.scale;
		const blocks: Array<Block> = planogram.stand.blocks;
		const products: Array<ProductGroup> = planogram.stand.products;

		const blocksMinX: number = Math.min(...blocks.map((block: Block) => block.begin[0].x)) * scale;
		const blocksMaxX: number = Math.max(...blocks.map((block: Block) => block.boundingX.max)) * scale;
		const planogramMaxX: number = planogram.beginPoint.x + planogram.width;
		const minX: number = Math.min(planogram.beginPoint.x, planogram.beginPoint.x + blocksMinX);
		const maxX: number = Math.max(planogramMaxX, planogram.beginPoint.x + blocksMaxX);

		const blocksMinY: number = Math.min(...blocks.map((block: Block) => block.begin[0].y)) * scale;
		const blocksMaxY: number = Math.max(...blocks.map((block: Block) => block.begin[0].y + block.height)) * scale;
		const productsMinY: number = (planogram.stand.Height - Math.max(...products.map((pg: ProductGroup) => pg.getBoundingY().max))) * scale;
		const minY: number = Math.min(planogram.beginPoint.y, planogram.beginPoint.y + blocksMinY, planogram.beginPoint.y + productsMinY);
		const maxY: number = Math.max(planogram.height, planogram.beginPoint.y + blocksMaxY)

		return {
			x: {
				min: Utils.round(minX + canvasOffset),
				max: Utils.round(maxX)
			},
			y: {
				min: Utils.round(minY + canvasOffset),
				max: Utils.round(maxY)
			},
			height: Utils.round(maxY - minY),
			width: Utils.round(maxX - minX)
		}
	}

	static getPlanogramDetailedBoundingWithBlocks(
		planogram: IPlanogram,
		canvasOffset: number
	): IPlanogramBoundingWithBlocks {
		const element: Element = planogram.detaledElement;
		const scale: number = planogram.detalisationScale;

		const minX: number = planogram.beginDetailPoint.x;
		const width: number = element.parent.type === 'Hook'
			? element.Depth
			: element.Width;

		const minY: number = planogram.beginDetailPoint.y;
		const height: number = element.parent.type === 'Hook'
			? planogram.height / scale
			: element.Depth;

		return {
			x: {
				min: Utils.round(minX + canvasOffset),
				max: Utils.round(minX + width * scale)
			},
			y: {
				min: Utils.round(minY + (element.parent.type === 'Hook' ? 0 : canvasOffset)),
				max: Utils.round(minY + height * scale)
			},
			width: Utils.round(width * scale),
			height: Utils.round(height * scale)
		}
	}

	static getProductRotateToDepth(product: any, productRotateTo: any, productRotateFrom: any): number {
		const productDepth: number = Math.min(product.placement_depth, product.qty) * productRotateFrom.depth;
		return Math.floor(productDepth / productRotateTo.depth);
	}

	static getProductRotateToHeight(productRotateFrom: any, product: any): number {
		return productRotateFrom.height * product.placement_height;
	}

	static getProductRotateToWidth(standProductRotateFrom: any, productRotateFrom: any, oldDepth: number): number {
		const findPacking = productRotateFrom.packings.length && standProductRotateFrom.packings ?
			productRotateFrom.packings.find(p => p.id == standProductRotateFrom.packings) : undefined;
		switch (standProductRotateFrom.rotation) {
			case ProductGroupRotation.Side: {
				return Math.ceil(standProductRotateFrom.qty / oldDepth) *
					(findPacking ? findPacking.depth : productRotateFrom.depth);
			}
			case ProductGroupRotation.FrontRotatedRight:
            case ProductGroupRotation.FrontRotatedLeft: {
				return Math.ceil(standProductRotateFrom.qty / oldDepth) *
					(findPacking ? findPacking.height : productRotateFrom.height);
			}
			default: {
				return Math.ceil(standProductRotateFrom.qty / oldDepth) *
					(findPacking ? findPacking.width : productRotateFrom.width);
			}
		}
	}

	static getProductSizes(product: Product, maxSize: number): { height: number, width: number } {
		let height: number,
			width: number;

		if (product) {
			if (product.previewHeight > product.previewWidth) {
				height = Math.min(product.previewHeight, maxSize);
				width = Math.floor(product.previewWidth * height / product.previewHeight);
			} else if (product.previewHeight < product.previewWidth) {
				width = Math.min(product.previewWidth, maxSize);
				height = Math.floor(product.previewHeight * width / product.previewWidth);
			} else {
				width = product.previewWidth;
				height = product.previewHeight;
			}
			return { height, width };
		}
	}

	static getProductRotateDepth(product: any, productRotateTo: any, productRotateFrom: any, shelfDepth?: number): number {
		const findPacking = productRotateFrom.packings.length && product.packings ?
			productRotateFrom.packings.find(p => p.id == product.packings) : undefined;

		switch (product.rotation) {
			case ProductGroupRotation.Side: {
				const productDepth = product.placement_depth * (findPacking ? findPacking.width : productRotateFrom.width);
				if (shelfDepth)
					// the depth of the product cannot be greater than the depth of the shelf
					return Math.min(
						Math.floor(productDepth / productRotateTo.width),
						Math.floor(shelfDepth / productRotateTo.width)
					);
				else return Math.floor(productDepth / productRotateTo.width);
			}
			case ProductGroupRotation.Top: {
				const productDepth = product.placement_depth * (findPacking ? findPacking.height : productRotateFrom.height);
				if (shelfDepth)
					// the depth of the product cannot be greater than the depth of the shelf
					return Math.min(
						Math.floor(productDepth / productRotateTo.height),
						Math.floor(shelfDepth / productRotateTo.height)
					);
				else return Math.floor(productDepth / productRotateTo.height);
			}
			default: {
				const productDepth = product.placement_depth * (findPacking ? findPacking.depth : productRotateFrom.depth);
				if (shelfDepth)
					// the depth of the product cannot be greater than the depth of the shelf
					return Math.min(
						Math.floor(productDepth / productRotateTo.depth),
						Math.floor(shelfDepth / productRotateTo.depth)
					);
				else return Math.floor(productDepth / productRotateTo.depth);
			}
		}
	}

	static getProductRotateToQty(
		product: any,
		productRotateFrom: any,
		productRotateTo: any,
		stand: any,
		standProductRotateFrom: any,
		oldDepth?: number
	): number {
		let productParams;
		switch (standProductRotateFrom.rotation) {
			case ProductGroupRotation.Side: {
				productParams = productRotateTo.depth;
				break;
			}
			case ProductGroupRotation.FrontRotatedRight:
            case ProductGroupRotation.FrontRotatedLeft: {
				productParams = productRotateTo.height;
				break;
			}
			default: {
				productParams = productRotateTo.width;
				break;
			}
		}

		const productWidth: number = Utils.getProductRotateToWidth(standProductRotateFrom, productRotateFrom, oldDepth),
			faces: number = Math.floor(productWidth / productParams) || 1;

		return product.placement_depth * faces || 1;
	}

	static getRandomColor(): string {
		const r = (Math.floor(Math.random() * 240) + 16).toString(16),
			g = (Math.floor(Math.random() * 240) + 16).toString(16),
			b = (Math.floor(Math.random() * 240) + 16).toString(16);

		return `#${r + g + b}`;
	}

	static getSortedBlocks(blocks: Array<Block>): Array<Block> {
		return blocks.sort((a: Block, b: Block) => b.boundingY.max - a.boundingY.max);
	}

	static getSortedElements(elements: Array<Element>): Array<Element> {
		return elements.sort((a: Element, b: Element) => b.getBoundingY().max - a.getBoundingY().max);
	}

	static getSortedElementsByDepend(elements: Array<Element>): Array<Element> {
		for (let i = 0; i < elements.length - 1; i++) {
			for (let j = 0; j < elements.length - 1; j++) {
				const { depend: dependA } = elements[j],
					{ depend: dependB } = elements[j + 1];
				if (elements.indexOf(dependA) > elements.indexOf(dependB)) {
					[elements[j], elements[j + 1]] = [elements[j + 1], elements[j]];
				}
			}
		}
		return elements;
	}

	static getSortedProducts(products: Array<ProductGroup>): Array<ProductGroup> {
		return products.sort((a: ProductGroup, b: ProductGroup) => b.getBoundingY().max - a.getBoundingY().max);
	}

	static groupBy<T = any>(list: Array<T>, by: string, sort: boolean = true): Array<any> {
		let tempList: Array<any> = [],
			resultList: Array<any> = [];

		if (sort) {
			list.sort((a: T, b: T) => {
				if (typeof (a[by]) == 'string' && typeof (b[by]) == 'string') {
					return a[by].localeCompare(b[by]);
				}
				return a[by] - b[by];
			});
		}

		for (let i = 0; i < list.length; i++) {
			tempList.push(list[i]);
			if (i != list.length - 1 && list[i][by] == list[i + 1][by]) continue;
			resultList.push(tempList);
			tempList = [];
		}

		return resultList;
	}

	static groupByChildProperty<T = any>(list: Array<T>, by: string, childBy: string, deepChildBy?: string): Array<any> {
		let tempList: Array<any> = [],
			resultList: Array<any> = [];

		list.sort((a: T, b: T) => {
			if (deepChildBy) {
				return a[by]?.[childBy]?.[deepChildBy] - b[by]?.[childBy]?.[deepChildBy];
			} else {
				return a[by]?.[childBy] - b[by]?.[childBy];
			}
		});

		for (let i = 0; i < list.length; i++) {
			tempList.push(list[i]);
			if (deepChildBy) {
				if (i != list.length - 1 && list[i][by]?.[childBy]?.[deepChildBy] == list[i + 1][by]?.[childBy]?.[deepChildBy]) continue;
			} else {
				if (i != list.length - 1 && list[i][by]?.[childBy] == list[i + 1][by]?.[childBy]) continue;
			}
			resultList.push(tempList);
			tempList = [];
		}

		return resultList;
	}

	static groupByValue(array: Array<any>, key: string): any {
		return array.reduce(
			(objectsByKeyValue, obj) => ({
				...objectsByKeyValue,
				[obj[key]]: (objectsByKeyValue[obj[key]] || []).concat(obj)
			}),
			{}
		);
	}

	static async heicToJpeg(file: File): Promise<File> {
		try {
			const buffer = await file.arrayBuffer();
			const result = await convert({
				buffer: new Uint8Array(buffer) as any,
				format: 'JPEG'
			});
		
			const fileName = file.name.replace(/\.[^/.]+$/, '') + '.jpeg';
			return new File([result], fileName, { type: 'image/jpeg' });
		} catch (error) {
			console.error('Error converting HEIC to JPEG:', error);
			return file;
		}
	}

	static async resizeImage(file: File): Promise<File> {
		const MAX_SIZE: number = 2048;
		const supportedMimeTypes: Array<string> = ['image/jpeg', 'image/png', 'image/webp'];

		if (!supportedMimeTypes.includes(file.type)) return file;

		try {
			const bitmap = await createImageBitmap(file);
			const { width, height } = bitmap;

			if (width <= MAX_SIZE && height <= MAX_SIZE) {
				bitmap.close();
				return file;
			}

			let newWidth: number, newHeight: number;
			if (width > height) {
				newWidth = MAX_SIZE;
				newHeight = (height / width) * MAX_SIZE;
			} else {
				newHeight = MAX_SIZE;
				newWidth = (width / height) * MAX_SIZE;
			}

			const canvas = document.createElement('canvas');
			canvas.width = newWidth;
			canvas.height = newHeight;
			const ctx = canvas.getContext('2d');
			
			if (!ctx) {
				bitmap.close();
				return file;
			}

			ctx.drawImage(bitmap, 0, 0, newWidth, newHeight);
			bitmap.close();

			const blob = await new Promise<Blob>((resolve) => canvas.toBlob(b => resolve(b), file.type, 0.8));
			if (!blob) return file;

			return new File([blob], file.name, { type: file.type });
		} catch (error) {
			console.error('Image resize failed using Canvas:', error);
			return file;
		}
	}

	static isEqualProductGroups(productGroup: ProductGroup, secondProductGroup: ProductGroup): boolean {
		const keys: Array<keyof ProductGroup> = ['Depth', 'FacesQty', 'Height', 'Width', 'rotation'];

		for (let k of keys) {
			if (productGroup[k] != secondProductGroup[k]) return false;
		}

		return Utils.equalObjects(productGroup.begin, secondProductGroup.begin, true);
	}

	static isNullOrUndefined<T = any>(value: T): boolean {
		return value === null || value === undefined;
	}

	static isRectangle(points: Array<Point>): boolean {
		if (points.length !== 4) return false;

		const [p1, p2, p3, p4] = points;

		return (
			Utils.distance(p1.x, p2.y, p2.x, p2.y) === Utils.distance(p3.x, p3.y, p4.x, p4.y) &&
			Utils.distance(p2.x, p2.y, p3.x, p3.y) === Utils.distance(p4.x, p4.y, p1.x, p1.y) &&
			Utils.distance(p1.x, p1.y, p3.x, p3.y) === Utils.distance(p2.x, p2.y, p4.x, p4.y)
		);
	}

    static isRightAngle(angle: number): boolean {
        if (!angle) return true;
        angle = angle % 360;
        if (angle < 0) angle += 360;
        return angle === 90 || angle === 180 || angle === 270;
    }

	static isSameDay(date1: Date, date2: Date): boolean {
		return (
			date1.getFullYear() === date2.getFullYear() &&
			date1.getMonth() === date2.getMonth() &&
			date1.getDate() === date2.getDate()
		);
	}

	static listIntersection<T>(list1: Array<T>, list2: Array<T>): Array<T> {
		return list1.filter((item: T) => list2.includes(item));
	}

	static max<T = number>(array: Array<T>, field?: keyof T): number {
		const key = x => field && typeof x === 'object' ? x[field] : x;

		return Math.max.apply(null, array.map(key));
	}

	static mean(arr: Array<number>, mapper?: Function): number {
		return arr.reduce((a: number, b: number) => a + (mapper ? mapper(b) : b), 0) / arr.length;
	}

	static min<T = number>(array: Array<T>, field?: keyof T): number {
		const key = x => field && typeof x === 'object' ? x[field] : x;

		return Math.min.apply(null, array.map(key));
	}

	static moveToPoint(coords: Array<any>, point: Point = { x: 0, y: 0 }): Array<any> {
		return coords.map(c => {
			for (let key in c) {
				if (typeof c[key] === 'object') {
					c[key].x = Utils.round(point.x + c[key].x, 4);
					c[key].y = Utils.round(point.y + c[key].y, 4);
				}
			}

			return c;
		});
	}

	static parentCheck<T extends IChecked<T> = any>(initElement: T): void {
		this.recUp(initElement, (element: T) => {
			if (element.parent) {
				const checkedChildren: Array<T> = element.parent.children.filter((child: T) => child.checked);
				element.parent.checked = element.parent.children.length === checkedChildren.length;
				element.parent.checkedShadow = checkedChildren.length > 0 || element.checkedShadow;
			}
			if (element.children) {
				element.checkedShadow = element.children.filter((child: T) => child.checked || child.checkedShadow).length > 0;
			}
			if (element.name == 'ALL' && !element.checked) {
				Utils.unSelectAll(initElement);
			}
		});
	}

    static parseIsoWithMicro(iso: string): number {
        if (iso == null || typeof iso !== 'string') {
            return 0;
        }
        const regex = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})\.(\d+)([+-]\d{2}:\d{2}|Z)$/;
        const match = iso.match(regex);
        if (!match) {
            return new Date(iso).getTime();
        }
        const [, datetime, micro, tz] = match;
        const baseMs = new Date(datetime + tz).getTime();
        const microNumber = parseInt(micro.padEnd(6, '0').slice(0, 6));
        const fractionalMs = microNumber / 1000;
        return baseMs + fractionalMs;
}

	static productIsRelevantForBlock(product: Product, block: IPlanogramBlockData): boolean {
		const errors: Array<boolean> = [
			block.categories.length && block.categories.every((category: number) =>
				!Utils.to1DArray(product.categories).map((c: ITreeProperty) => c.id).includes(category)
			),
			block.markers.length && block.markers.every((marker: number) =>
				!Utils.to1DArray(product.markers).map((m: ITreeProperty) => m.id).includes(marker)
			),
			block.suppliers.length && block.suppliers.every((supplier: number) =>
				!product.suppliers.map((s: IProperty) => s.id).includes(supplier)
			),
			block.brands.length && block.brands.every((brand: number) =>
				!product.brands.map((b: IProperty) => b.id).includes(brand)
			),
			block.assortment_types.length && !block.assortment_types.includes(product.assortment_type.id),
			block.product_statuses.length && block.product_statuses.every((status: number) =>
				!product.statuses.map((s: IProperty) => s.id).includes(status)
			)
		];

		return !errors.some((condition: boolean) => condition);
	}

	static randomElementFromArray<T = any>(list: Array<T>): T {
		return list[Math.floor(Math.random() * list.length)];
	}

	static rec<T extends IChildren<T> = any>(tree: Array<T>, callback: (element: T) => void): void {
		if (!tree) return;

		tree.forEach((element: T) => {
			callback(element);
			if (element.hasOwnProperty('children') && Array.isArray(element.children) && element.children.length) {
				Utils.rec(element.children, callback);
			}
		});
	}

	static recUp<T extends IParent<T> = any>(element: T, callback: (element: T) => void): void {
		if (!element) return;

		callback(element);
		if (element.hasOwnProperty('parent') && element.parent) {
			Utils.recUp(element.parent, callback);
		}
	}

	static recursiveOpened<T extends IOpened<T> = any>(elements: Array<T>): void {
		this.rec(elements, (element: T) => {
			element.opened = !!element.children && !!element.children.length;
		});
	}

	static rotateInBoxes(product: any, stand: any): boolean {
		// якщо товар для заміни ваговий && елемент==Box && в ньому тільки один товар - тоді заміняєм
		return ['Box', 'SlicedBox'].includes(stand.instrument.conf.elements[product.element_id].parent) &&
			stand.products.filter(pg => pg.element_id == product.element_id).length === 1;
	}

	static rotateOnHooks(product: any, stand: any): boolean {
		return stand.instrument.conf.elements[product.element_id].parent == 'Hook';
	}

	static rotateOnHoles(product: any, stand: any): boolean {
		return stand.instrument.conf.elements[product.element_id].parent == 'Hole';
	}

	static rotatePoint(center: [number, number], point: [number, number], angle: number): [number, number] {
		const rotationX: number = center[0],
			rotationY: number = center[1];

		angle = angle * Math.PI / 180;

		return [
			Utils.round((point[0] - rotationX) * Math.cos(angle) - (point[1] - rotationY) * Math.sin(angle) + rotationX),
			Utils.round((point[0] - rotationX) * Math.sin(angle) + (point[1] - rotationY) * Math.cos(angle) + rotationY)
		];
	}

	static reverseBlockDrawCoordinates(pointBegin: Point, pointEnd: Point): { x1: number; y1: number; x2: number; y2: number } {
		let bpX = pointBegin.x, bpY = pointBegin.y, lpX = pointEnd.x, lpY = pointEnd.y, delta: number;

		if (bpX > lpX) { delta = Math.abs(bpX - lpX); bpX = lpX; lpX = bpX + delta; }
		if (bpY > lpY) { delta = Math.abs(bpY - lpY); bpY = lpY; lpY = bpY + delta; }

		return { x1: bpX, y1: bpY, x2: lpX, y2: lpY };
	}

	static rotateProducts(
		standProduct: any,
		productRotateTo: any,
		productRotateFrom: any,
		standProductRotateFrom: any,
		instrument: any
	): any {
		const oldDepth: number = standProduct.placement_depth,
			productElement = instrument.conf.elements[standProductRotateFrom.element_id];

		if (productRotateFrom.is_loose && ['Box', 'SlicedBox'].includes(productElement.parent)) {
			standProduct.placement_depth = Math.floor(
				(productElement.shapes[0].depth - productElement.shapes[0].wallWidth * 2) / productRotateTo.depth
			);
			standProduct.qty = standProduct.placement_depth * Math.floor(
				(productElement.shapes[0].width - productElement.shapes[0].wallWidth * 2) / productRotateTo.width
			);
		} else if (productElement.parent == 'HangBar') {
			standProduct.placement_depth = 1;
			standProduct.qty = this.getProductRotateToQty(
				standProduct, productRotateFrom, productRotateTo, standProduct, standProductRotateFrom, oldDepth
			) || 1;
		} else {
			standProduct.placement_depth = this.getProductRotateDepth(
				standProduct, productRotateTo, productRotateFrom, productElement.shapes[0].depth
			) || 1;
			standProduct.qty = this.getProductRotateToQty(
				standProduct, productRotateFrom, productRotateTo, standProduct, standProductRotateFrom, oldDepth
			) || 1;
		}

		standProduct.angle = standProductRotateFrom.angle ?? GlobalSettings.DEFAULT_PRODUCT_GROUP_ANGLE;

		standProduct.master_product = productRotateTo.id;

		return standProduct;
	}

	static rotateProductOnHook(standProduct: any, productRotateFrom: any, productRotateTo: any, stand: any): any {
		standProduct.master_product = productRotateTo.id;
		standProduct.qty = Utils.getProductRotateDepth(
			standProduct, productRotateTo, productRotateFrom
		) || 1;
		standProduct.placement_depth = standProduct.qty;
		standProduct.position_y = (stand.instrument.conf.elements[standProduct.element_id].shapes[0].begin.y - productRotateTo.height)
			.toFixed(4);
		standProduct.position_x = (stand.instrument.conf.elements[standProduct.element_id].shapes[0].begin.x - (productRotateTo.width / 2))
			.toFixed(4);

		return standProduct;
	}

	static rotateProductOnHole(standProduct: any, productRotateFrom: any, productRotateTo: any, stand: any): any {
		standProduct.master_product = productRotateTo.id;
		standProduct.qty = productRotateFrom.qty;
		standProduct.placement_depth = 1;

		standProduct.position_y = (stand.instrument.conf.elements[standProduct.element_id].shapes[0].begin.y - productRotateTo.height)
			.toFixed(4);
		standProduct.position_x = (stand.instrument.conf.elements[standProduct.element_id].shapes[0].begin.x - (productRotateTo.width / 2))
			.toFixed(4);

		return standProduct;
	}

	static round(value: number, positions: number = GlobalSettings.ACCURACY): number {
		const roundMultiplier: number = Math.pow(10, positions);
		return Math.round(value * roundMultiplier) / roundMultiplier;
	}

	static roundToFixed(value: number, precision: number = GlobalSettings.ACCURACY): number {
		return +(+(Math.round(+(value + 'e' + precision)) + 'e' + -precision)).toFixed(precision);
	}

	static searchExactAlgorithm<T>(element: T, searchFields: Array<string | number>, searchQuery: string): boolean {
		return searchFields.some((field: string | number) => {
			const value = element[field];
			if (typeof value === 'string' || typeof value === 'number') {
				const stringValue: string = value.toString().toLowerCase().trim();
				return stringValue.includes(searchQuery);
			}
			return false;
		});
	}

	static searchKeywordAlgorithm<T>(element: T, searchFields: Array<string | number>, searchQuery: string): boolean {
		const keywords: Array<string> = searchQuery
			.replace(/,/g, ' ') // replace commas with spaces
			.split(' ') // divide into keywords by spaces
			.filter((query: string) => query); // remove empty elements

		if (!keywords.length) return true;

		return searchFields.some((field: string | number) => {
			const value = element[field];

			if (typeof value === 'string' || typeof value === 'number') {
				const stringValue: string = value.toString().toLowerCase().trim();
				// check whether all keyword appears in the value of the field
				return keywords.every((keyword: string) => stringValue.includes(keyword.trim()));
			}
			return false;
		});
	}

	static searchItem<T>(
		element: T,
		searchFields: Array<string | number>,
		searchQuery: string,
		searchAlgorithm: SearchAlgorithmType = 'exact'
	): boolean {
		searchQuery = (searchQuery ?? '')
			.toString()
			.toLowerCase()
			.trim();
		if (!searchQuery || !element) return true;

		if (searchAlgorithm === 'exact') {
			return Utils.searchExactAlgorithm<T>(element, searchFields, searchQuery);
		} else {
			return Utils.searchKeywordAlgorithm<T>(element, searchFields, searchQuery);
		}
	}

	static searchObjects<T = any>(
		elements: Array<T>,
		searchFields: Array<string | number>,
		searchQuery: string,
		searchAlgorithm: SearchAlgorithmType = 'exact'
	): Array<T> {
		if (!elements?.length) return [];
		searchQuery = (searchQuery ?? '')
			.toString()
			.toLowerCase()
			.trim();
		if (!searchQuery) return elements.concat();

		if (searchAlgorithm === 'exact') {
			return elements.filter((element: T) => Utils.searchExactAlgorithm(element, searchFields, searchQuery));
		} else {
			return elements.filter((element: T) => Utils.searchKeywordAlgorithm(element, searchFields, searchQuery));
		}
	}

	static setElementsOrder(elements: Array<Element>): Array<Element> {
		const sortedElements = elements.sort((a, b) => a.shapes[0].begin.x - b.shapes[0].begin.x),
			showCases = sortedElements.filter(el => el.parent.type == 'Showcase'),
			sidesWallsInner = sortedElements.filter(el =>
				(el.parent.type == EquipmentElements.SideWalls && el.depend.parent.type != EquipmentElements.BaseTrapeze) ||
				(el.parent.type == EquipmentElements.SideWalls && el.depend.parent.type == EquipmentElements.BaseTrapeze && (el.depend.shapes[0] as Trapeze).backWidth > (el.depend.shapes[0] as Trapeze).frontWidth)
			),
			sidesWallsOuter = sortedElements.filter(el =>
				el.parent.type == EquipmentElements.SideWalls && el.depend.parent.type == EquipmentElements.BaseTrapeze && (el.depend.shapes[0] as Trapeze).backWidth < (el.depend.shapes[0] as Trapeze).frontWidth
			),
			trapezeShelves = sortedElements.filter(el => el.parent.type == 'Shelf' && el.shapes[0].type == 'trapeze'),
			shelves = sortedElements.filter(el => ['AngleShelf', 'Shelf'].includes(el.parent.type) && el.shapes[0].type == 'rectangle'),
			hangBars = sortedElements.filter(el => ['HangBar'].includes(el.parent.type)),
			bases = sortedElements.filter(el => ['RectangleBase', 'TrapezeBase'].includes(el.parent.type)),
			trapezeBoxes = sortedElements.filter(el => ['Box', 'SlicedBox'].includes(el.parent.type) && el.depend.shapes[0].type == 'trapeze'),
			boxes = sortedElements.filter(el => ['Box', 'SlicedBox'].includes(el.parent.type) && el.depend.shapes[0].type !== 'trapeze'),
			hookPanels = sortedElements.filter(el => el.parent.type == 'HookPanel'),
			hooks = sortedElements.filter(el => el.parent.type == 'Hook'),
			holes = sortedElements.filter(el => el.parent.type == 'Hole'),
			partitions = sortedElements.filter(el => el.parent.type == 'Partition'),
			filteredElements = [
				...showCases,
				...bases,
				...sidesWallsOuter,
				...hangBars,
				...hookPanels,
				...hooks,
				...holes,
				...shelves,
				...boxes,
				...trapezeShelves,
				...trapezeBoxes,
				...partitions,
				...sidesWallsInner
			],
			otherElements = sortedElements.filter(el => !filteredElements.includes(el));
		return [
			...showCases,
			...otherElements,
			...bases,
			...sidesWallsOuter,
			...hangBars,
			...hookPanels,
			...hooks,
			...holes,
			...shelves,
			...boxes,
			...trapezeShelves,
			...trapezeBoxes,
			...partitions,
			...sidesWallsInner
		];
	}

	static setParent<T extends IParent<T>>(elements: Array<T>): void {
		this.rec(elements, (element: T) => {
			if (element.children && element.children.length) {
				element.children.forEach(child => child.parent = element);
			}
		});
	}

	static shuffle<T = any>(array: Array<T>): Array<T> {
		let currentIndex: number = array.length,
			temporaryValue: T,
			randomIndex: number;

		while (0 !== currentIndex) {
			randomIndex = Math.floor(Math.random() * currentIndex);
			currentIndex -= 1;

			temporaryValue = array[currentIndex];
			array[currentIndex] = array[randomIndex];
			array[randomIndex] = temporaryValue;
		}

		return array;
	}

	static sortBy<T = any>(field: string, order: 'asc' | 'desc' = 'asc'): (a: T, b: T) => number {
		const key = (x: T) => x[field],
			reverse: number = order == 'asc' ? 1 : -1;

		return function (a: T, b: T) {
			a = typeof key(a) == 'string' ? key(a).toLowerCase() : key(a);
			b = typeof key(b) == 'string' ? key(b).toLowerCase() : key(b);

			return reverse * (((a > b) as any) - ((b > a) as any));
		};
	}

	static sortData(index: number, order: 'asc' | 'desc' | string, key?: string, dateFormat?: string): (a: any, b: any) => number {
		return (a, b) => {
			if (key) {
				a = a[key];
				b = b[key];
			}

			return Utils.generalSorting({
				firstEl: a[index],
				secondEl: b[index],
				order: order,
				dateFormat: dateFormat
			});
		};
	}

	static sortingBarcode(sortParam: ISortParameters): number {
		const aBarcodeValue = Number(sortParam.firstEl.split(',')[0]),
			bBarcodeValue = Number(sortParam.secondEl.split(',')[0]);

		if (!isNaN(Number(aBarcodeValue)) || !isNaN(Number(bBarcodeValue))) {
			if (aBarcodeValue < bBarcodeValue) {
				return sortParam.order == 'desc' ? -1 : 1;
			} else {
				if (aBarcodeValue > bBarcodeValue) {
					return sortParam.order == 'desc' ? 1 : -1;
				}
			}
			return 0;
		} else {
			return sortParam.order == 'desc' ?
				sortParam.firstEl.localeCompare(sortParam.secondEl) :
				sortParam.firstEl.localeCompare(sortParam.secondEl);
		}
	}

	private static parseSortableDate(value: unknown, dateFormat: string = DEFAULT_DATE_FORMAT): Date | null {
		if (value instanceof Date) {
			return isNaN(value.getTime()) ? null : value;
		}

		if (typeof value !== 'string') return null;

		const trimmed: string = value.trim();
		if (!trimmed) return null;

		const dateLikeRegExp: RegExp =
			/^(?:\d{4}[-/]\d{1,2}[-/]\d{1,2}|\d{1,2}[./-]\d{1,2}[./-]\d{2,4})(?:[T\s]\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;
		if (!dateLikeRegExp.test(trimmed)) return null;

		return this.parseDateString(trimmed, dateFormat);
	}

	private static parseDateString(value: string, dateFormat: string = DEFAULT_DATE_FORMAT): Date | null {
		const normalized: string = value.replace(/^(\d{4})\/(\d{2})\/(\d{2})(?=[Tt ]|$)/, '$1-$2-$3');
		const yearFirstMatch: RegExpMatchArray | null = normalized.match(/^(\d{4})[./-](\d{1,2})[./-](\d{1,2})(.*)$/);

		if (yearFirstMatch) {
			if (!yearFirstMatch[4]) {
				return this.localCalendarDateOrNull(
					parseInt(yearFirstMatch[1], 10),
					parseInt(yearFirstMatch[2], 10) - 1,
					parseInt(yearFirstMatch[3], 10)
				);
			}

			const nativeValue: string = normalized
				.replace(/^(\d{4}-\d{2}-\d{2})\s+/, '$1T')
				.replace(/(\.\d{3})\d+Z$/i, '$1Z');
			const hasTimeZone: boolean = /(?:[zZ]|[+-](?:\d{2}:\d{2}|\d{4}))$/.test(nativeValue);
			const parseValue: string = hasTimeZone ? nativeValue : `${nativeValue}Z`;
			const date: Date = new Date(parseValue);
			return isNaN(date.getTime()) ? null : date;
		}

		const yearLastMatch: RegExpMatchArray | null = normalized.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
		if (!yearLastMatch) return null;

		const a: number = parseInt(yearLastMatch[1], 10),
			b: number = parseInt(yearLastMatch[2], 10),
			yearRaw: number = parseInt(yearLastMatch[3], 10),
			year: number = yearRaw < 100 ? 2000 + yearRaw : yearRaw;
		let day: number,
			monthIndex: number;

		if (a > 12) {
			day = a;
			monthIndex = b - 1;
		} else if (b > 12 || dateFormat === 'MM-DD-YYYY') {
			day = b;
			monthIndex = a - 1;
		} else {
			day = a;
			monthIndex = b - 1;
		}

		return this.localCalendarDateOrNull(year, monthIndex, day);
	}

	private static localCalendarDateOrNull(year: number, monthIndex: number, day: number): Date | null {
		const date: Date = new Date(year, monthIndex, day);
		if (
			isNaN(date.getTime()) ||
			date.getFullYear() !== year ||
			date.getMonth() !== monthIndex ||
			date.getDate() !== day
		) {
			return null;
		}

		return date;
	}

	static sortingDates(sortParam: ISortParameters): number {
		const date_a: Date | null = this.parseSortableDate(sortParam.firstEl, sortParam.dateFormat),
			date_b: Date | null = this.parseSortableDate(sortParam.secondEl, sortParam.dateFormat);

		if (date_a && date_b) {
			if (date_a < date_b) {
				return sortParam.order == 'desc' ? -1 : 1;
			}

			if (date_a > date_b) {
				return sortParam.order == 'desc' ? 1 : -1;
			}

			return 0;
		}

		if (sortParam.firstEl < sortParam.secondEl) {
			return sortParam.order == 'desc' ? -1 : 1;
		}

		if (sortParam.firstEl > sortParam.secondEl) {
			return sortParam.order == 'desc' ? 1 : -1;
		}

		return 0;
	}

	static sortingNumbers(sortParam: ISortParameters): number {
		if (['', null, 'GENERAL'].includes(sortParam.firstEl)) {
			sortParam.firstEl = '-100000000000';
		}

		if (['', null, 'GENERAL'].includes(sortParam.secondEl)) {
			sortParam.secondEl = '-100000000000';
		}
		if (Number(sortParam.firstEl) < Number(sortParam.secondEl)) {
			return sortParam.order == 'desc' ? -1 : 1;
		} else {
			if (Number(sortParam.firstEl) > Number(sortParam.secondEl)) {
				return sortParam.order == 'desc' ? 1 : -1;
			}
		}
		return 0;
	}

	static sortPlanogramData(sortParam: ISortParameters): number {
		if (sortParam.disabledFirstEl == false && sortParam.disabledSecondEl == false) {
			return Utils.generalSorting({
				firstEl: sortParam.firstEl,
				secondEl: sortParam.secondEl,
				order: sortParam.order,
				displayValue: sortParam.displayValue,
				dateFormat: sortParam.dateFormat
			});
		}

		if (sortParam.disabledFirstEl == true && sortParam.disabledSecondEl == true) {
			return Utils.generalSorting({
				firstEl: sortParam.firstEl,
				secondEl: sortParam.secondEl,
				order: sortParam.order,
				displayValue: sortParam.displayValue,
				dateFormat: sortParam.dateFormat
			});
		} else {
			return 0;
		}
	}

	static sortShelves(): (a: Element, b: Element) => number {
		return (a: Element, b: Element) => {
			if (Math.abs(b.shapes[0].begin.y - a.shapes[0].begin.y) < .01) {
				return a.shapes[0].begin.x - b.shapes[0].begin.x;
			}
			return b.shapes[0].begin.y - a.shapes[0].begin.y;
		};
	}

	static splitArrayByType<T = any>(list: Array<T>): Array<Array<T>> {
		const output: Array<Array<T>> = [];
		let temp: Array<T> = [];

		list.forEach((element: T) => {
			if (!temp.length) {
				temp.push(element);
				return;
			}

			if (element.constructor.name == temp[0].constructor.name) {
				temp.push(element);
				return;
			}

			output.push([...temp]);
			temp = [element];
		});

		output.push([...temp]);
		return output.filter(i => i.length);
	}

	static svgToBase64(svgString: string): string {
		const chunkSize: number = 65536, // Максимальна кількість аргументів, яку може обробити fromCharCode
			encoder: TextEncoder = new TextEncoder(),
			data: Uint8Array = encoder.encode(svgString),
			chunks: Array<any> = [];

		for (let i = 0; i < data.length; i += chunkSize) {
			const chunk: Uint8Array = data.slice(i, i + chunkSize),
			encodedChunk = String.fromCharCode.apply(null, chunk);
			chunks.push(encodedChunk);
		}

		const base64Svg: string = btoa(chunks.join(''));
		return `data:image/svg+xml;base64,${base64Svg}`;
	}

	static sum(list: Array<number>, positions: number = GlobalSettings.ACCURACY): number {
		return Utils.round(list.reduce((a: number, b: number) => a + b, 0), positions);
	}

	static to1DArray(list: Array<any>): any {
		return list.flat(Infinity);
	}

	static transposeVariableLengthArray(array: Array<any>): any {
		return array.reduce((r, a, i, { length }) => {
			a.forEach((v, j) => {
				r[j] = r[j] || new Array(length).fill('');
				r[j][i] = v;
			});

			return r;
		}, []);
	}

	static unique<T = any>(elements: Array<T>): Array<T> {
		const output: Array<T> = [];

		elements.forEach((element: T) => {
			if (!output.includes(element)) output.push(element);
		});

		return output;
	}

	static unSelectAll<T extends IChecked<T> = any>(element: T): void {
		element.checkedShadow = false;
		if (element.children && element.children.length) {
			element.children.forEach((child: T) => Utils.unSelectAll(child));
		}
	}

	static validate(params: any): any {
		const validatedParams: any = {};
		for (let key in params) {
			if (params[key] != null) validatedParams[key] = params[key];
		}
		return validatedParams;
	}

	static isWebGLSupported(): boolean {
		const canvas: HTMLCanvasElement = document.createElement('canvas');
		const gl: RenderingContext = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
		return !!gl;
	}

	static isExperimentalWebGL(): boolean {
		const canvas: HTMLCanvasElement = document.createElement('canvas');
		const gl: RenderingContext = canvas.getContext('experimental-webgl');
		return !!gl;
	}

	static isBrowserExperimental(): boolean {
		const userAgent: string = navigator.userAgent.toLowerCase();

		if (userAgent.includes('chrome') && userAgent.includes('experimental')) {
			return true;
		}

		const canvas: HTMLCanvasElement = document.createElement('canvas');
		const gl = canvas.getContext('experimental-webgl') as WebGLRenderingContext;
		if (gl) {
			const extensions: Array<string> = gl.getSupportedExtensions();
			if (extensions && extensions.length > 0) {
				const experimentalExtensions: Array<string> = extensions.filter((ext: string): boolean =>
					ext.includes('experimental') || ext.includes('draft')
				);
				return experimentalExtensions.length > 0;
			}
		}

		return false;
	}

	static isWebGLWorking(sessionService?: any): boolean {
		if (sessionService && sessionService.webglWorking !== null) {
			return sessionService.webglWorking;
		}

		if (!Utils.isWebGLSupported()) {
			return false;
		}
		
		if (Utils.isBrowserExperimental()) {
			console.warn('Browser uses experimental WebGL features, which may cause issues');
		}
		
		return true;
	}

	static testWebGLSupport(): boolean {
		try {
			const canvas: HTMLCanvasElement = document.createElement('canvas');
			const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext;
			
			if (!gl) {
				return false;
			}

			const testProgram: WebGLProgram = gl.createProgram();
			if (!testProgram) {
				return false;
			}
			
			const debugInfo: WEBGL_debug_renderer_info = gl.getExtension('WEBGL_debug_renderer_info');
			if (debugInfo) {
				const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
				const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);

				if (!renderer || renderer.includes('unknown') || renderer.includes('null')) {
					console.warn('Browser cannot identify graphics card:', { renderer, vendor });
					return false;
				}
			}

			gl.deleteProgram(testProgram);
			return true;
		} catch (e) {
			console.error('Error checking WebGL:', e);
			return false;
		}
	}
}
